import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import { Mail, Lock, User, Quote, Eye, EyeOff, AtSign } from 'lucide-react';
import { ArshHeader } from '../components/ArshHeader';
import { ANIMAL_ICONS } from '../theme';

const PILLAR_ICONS = [
  { id: 'fashion', beast: 'Leopard',  label: 'Fashion' },
  { id: 'food',    beast: 'Gazelle',  label: 'Taste'   },
  { id: 'explore', beast: 'Falcon',   label: 'Explore' },
  { id: 'souls',   beast: 'Phoenix',  label: 'Soul'    },
  { id: 'sporty',  beast: 'Gorilla',  label: 'Sport'   },
  { id: 'success', beast: 'Stallion', label: 'Success' },
  { id: 'legacy',  beast: 'Lion',     label: 'Legacy'  },
  { id: 'art',     beast: 'Crane',    label: 'Art'     },
  { id: 'dragon',  beast: 'Dragon',   label: 'Empire'  },
];

const fadeInUp = { initial: { opacity: 0, y: 30 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.8 } };

const formatApiErrorDetail = (detail) => {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
};

export default function Register() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [nickname, setNickname] = useState('');
  const [blissId, setBlissId] = useState('');
  const [mode, setMode] = useState('email');
  const [lifeQuote, setLifeQuote] = useState('');
  const [profileTag, setProfileTag] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!profileTag) { setError('Please select your pillar icon to continue.'); return; }
    setLoading(true);
    try {
      await register(mode === 'email' ? email : null, password, nickname, lifeQuote, profileTag, mode === 'bliss' ? blissId : null);
      navigate('/');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const inputCls = "w-full pl-12 pr-4 py-4 rounded-xl border border-[#D4AF37]/15 bg-white/5 focus:outline-none focus:border-[#D4AF37]/40 focus:ring-1 focus:ring-[#D4AF37]/30 font-['Outfit'] text-white/90 placeholder-white/20 transition-all duration-300";

  return (
    <div className="min-h-screen flex flex-col lg:flex-row" style={{ background: '#000000' }}>
      {/* Left Side - Hero */}
      <div className="hidden lg:block lg:w-1/2 relative overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1774359535371-c9ea5ad2e8ec?crop=entropy&cs=srgb&fm=jpg&q=85)' }}>
          <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-black/50 to-transparent" />
        </div>
        <div className="relative h-full flex flex-col justify-end p-12 text-white">
          <motion.div {...fadeInUp}>
            <h1 className="font-['Cormorant_Garamond'] text-6xl font-light mb-4 leading-tight tracking-tight">
              Start Your <br /><span className="text-[#D4AF37]">Vibe Collection</span>
            </h1>
            <p className="font-['Outfit'] text-lg text-white/50 max-w-md">
              Join the ranks. Capture your adventures in fashion, food, and exploration.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Right Side - Register Form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12" style={{ background: '#05050A' }}>
        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} className="w-full max-w-md">
          <div className="text-center mb-8">
            <ArshHeader />
            <p className="font-['Outfit'] text-white/30 text-sm mt-2">Your journey starts here</p>
          </div>

          {error && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
              className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-xl mb-6 font-['Outfit'] text-sm"
              data-testid="register-error"
            >{error}</motion.div>
          )}

          <button onClick={handleGoogleLogin}
            className="w-full flex items-center justify-center gap-3 px-6 py-4 border border-white/10 rounded-xl font-['Outfit'] font-medium text-white/70 hover:border-[#D4AF37]/30 hover:bg-white/5 transition-all duration-300 mb-6"
            data-testid="google-register-button"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </button>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/10" /></div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 font-['Outfit'] text-white/20" style={{ background: '#05050A' }}>Or register with email or Bliss ID</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} data-testid="register-form" className="space-y-4">
            <div className="grid grid-cols-2 gap-1 p-1 rounded-full border border-[#D4AF37]/20 bg-white/[0.03]" data-testid="register-mode-toggle">
              {[['email', 'Email'], ['bliss', 'Bliss ID']].map(([k, label]) => (
                <button key={k} type="button" onClick={() => setMode(k)}
                  className="py-2.5 rounded-full font-['Outfit'] text-[11px] tracking-[0.18em] uppercase font-semibold transition-colors duration-300"
                  style={mode === k
                    ? { background: '#D4AF37', color: '#000', boxShadow: '0 0 14px rgba(212,175,55,0.35)' }
                    : { background: 'transparent', color: 'rgba(212,175,55,0.6)' }}
                  aria-pressed={mode === k} data-testid={`register-mode-${k}`}>{label}</button>
              ))}
            </div>
            {mode === 'email' ? (
              <div>
                <label className="block font-['Outfit'] text-[10px] tracking-[0.15em] uppercase font-bold text-[#D4AF37]/60 mb-2">Email Address</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-white/20" size={20} />
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className={inputCls} placeholder="your@email.com" data-testid="register-email-input" />
                </div>
              </div>
            ) : (
              <div>
                <label className="block font-['Outfit'] text-[10px] tracking-[0.15em] uppercase font-bold text-[#D4AF37]/60 mb-2">Bliss ID</label>
                <div className="relative">
                  <AtSign className="absolute left-4 top-1/2 -translate-y-1/2 text-white/20" size={20} />
                  <input type="text" value={blissId} required minLength={3} maxLength={20} autoCapitalize="none" autoCorrect="off"
                    onChange={(e) => setBlissId(e.target.value.toLowerCase().replace(/[^a-z0-9._]/g, ''))}
                    className={inputCls} placeholder="your.blissid" data-testid="register-blissid-input" />
                </div>
                <p className="font-['Outfit'] text-[11px] text-white/30 mt-1.5">3–20 characters · letters, numbers, . and _ · no email means password reset isn't available</p>
              </div>
            )}
            <div>
              <label className="block font-['Outfit'] text-[10px] tracking-[0.15em] uppercase font-bold text-[#D4AF37]/60 mb-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-white/20" size={20} />
                <input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} required className={`${inputCls} pr-12`} placeholder="••••••••" data-testid="register-password-input" />
                <button type="button" onClick={() => setShowPassword((v) => !v)} className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-[#D4AF37] transition-colors" aria-label={showPassword ? 'Hide password' : 'Show password'} data-testid="register-password-toggle">
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
            <div>
              <label className="block font-['Outfit'] text-[10px] tracking-[0.15em] uppercase font-bold text-[#D4AF37]/60 mb-2">Nickname</label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 text-white/20" size={20} />
                <input type="text" value={nickname} onChange={(e) => setNickname(e.target.value)} required className={inputCls} placeholder="Your nickname" data-testid="register-nickname-input" />
              </div>
            </div>
            <div>
              <label className="block font-['Outfit'] text-[10px] tracking-[0.15em] uppercase font-bold text-[#D4AF37]/60 mb-2">Life Quote (Optional)</label>
              <div className="relative">
                <Quote className="absolute left-4 top-4 text-white/20" size={20} />
                <textarea value={lifeQuote} onChange={(e) => setLifeQuote(e.target.value)}
                  className={`${inputCls} resize-none`} placeholder="Your life motto..." rows="2" data-testid="register-lifequote-input" />
              </div>
            </div>

            <div>
              <label className="block font-['Outfit'] text-[10px] tracking-[0.15em] uppercase font-bold text-[#D4AF37]/60 mb-2">
                Your Pillar <span className="text-[#D4AF37]">*</span>
              </label>
              <p className="font-['Cormorant_Garamond'] italic text-white/40 text-sm mb-3">Choose the beast that guards your reign.</p>
              <div className="grid grid-cols-3 gap-2" data-testid="register-pillar-grid">
                {PILLAR_ICONS.map((p) => {
                  const icon = ANIMAL_ICONS[p.id];
                  const active = profileTag === p.id;
                  return (
                    <button
                      type="button"
                      key={p.id}
                      onClick={() => setProfileTag(p.id)}
                      data-testid={`register-pillar-${p.id}`}
                      className={`flex flex-col items-center rounded-xl py-2.5 px-1 transition-all ${active ? 'bg-[#D4AF37]/20 border border-[#D4AF37]' : 'border border-white/8 hover:border-[#D4AF37]/30'}`}
                    >
                      <div className="w-10 h-10 rounded-full overflow-hidden border border-black/20 mb-1"
                        style={active ? { boxShadow: '0 0 12px rgba(212,175,55,0.5)' } : {}}>
                        {icon && <img src={icon.src} alt={p.beast} className="w-full h-full object-cover" style={{ objectPosition: icon.pos }}/>}
                      </div>
                      <div className={`font-['Cormorant_Garamond'] text-xs ${active ? 'text-[#D4AF37]' : 'text-white/70'}`}>{p.beast}</div>
                      <div className="font-['Outfit'] text-[8px] uppercase tracking-[0.15em] text-white/40">{p.label}</div>
                    </button>
                  );
                })}
              </div>
            </div>
            {error && <p className="font-['Outfit'] text-sm text-red-400" data-testid="register-error-inline">{error}</p>}
            <button type="submit" disabled={loading}
              className="w-full bg-[#D4AF37] text-black py-4 rounded-xl font-['Outfit'] font-semibold hover:shadow-[0_0_20px_rgba(212,175,55,0.3)] transform hover:scale-[1.02] transition-all duration-300 disabled:opacity-50 disabled:transform-none"
              data-testid="register-submit-button"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                  Creating account...
                </span>
              ) : 'Claim Your Rank'}
            </button>
          </form>

          <p className="mt-8 text-center font-['Outfit'] text-white/30">
            Already have an account?{' '}
            <Link to="/login" className="text-[#D4AF37]/70 hover:text-[#D4AF37] font-semibold transition-colors" data-testid="register-login-link">Sign in</Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
